import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState, useEffect } from "react";
import { toast } from "sonner";

const LEVEL_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "BEGINNER", label: "Beginner" },
  { value: "INTERMEDIATE", label: "Intermediate" },
  { value: "ADVANCED", label: "Advanced" },
  { value: "PROFESSIONAL", label: "Professional" },
];

const SIDE_OPTIONS = [
  { value: "", label: "No preference" },
  { value: "LEFT", label: "Left (Drive)" },
  { value: "RIGHT", label: "Right (Revés)" },
  { value: "BOTH", label: "Both" },
];

const TIME_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "EARLY_MORNING", label: "Early Morning (6\u201309)" },
  { value: "MORNING", label: "Morning (9\u201312)" },
  { value: "AFTERNOON", label: "Afternoon (12\u201317)" },
  { value: "EVENING", label: "Evening (17\u201321)" },
];

const INTEREST_OPTIONS = [
  "PADEL",
  "TENNIS",
  "GOLF",
  "DINING",
  "SOCIAL_EVENTS",
  "WELLNESS",
  "TRAVEL",
];

export default function Preferences() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["member-preferences"],
    queryFn: () => api.get("/member/preferences"),
  });

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data && !form) {
      setForm({
        playing_level: data.playing_level || "",
        preferred_side: data.preferred_side || "",
        typical_playing_time: data.typical_playing_time || "",
        interests: data.interests || [],
        open_to_game_invitations: data.open_to_game_invitations ?? true,
      });
    }
  }, [data, form]);

  const mutation = useMutation({
    mutationFn: (payload) => api.put("/member/preferences", payload),
    onSuccess: () => {
      toast.success("Preferences saved");
      queryClient.invalidateQueries({ queryKey: ["member-preferences"] });
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="preferences-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6" data-testid="preferences-error">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const handleSelect = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const toggleInterest = (interest) =>
    setForm((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      playing_level: form.playing_level || null,
      preferred_side: form.preferred_side || null,
      typical_playing_time: form.typical_playing_time || null,
    });
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="preferences-page">
      <div className="mb-10 sm:mb-14">
        <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-wide">
          Preferences
        </h1>
        <p className="text-sm text-lv-muted tracking-wide mt-2">
          Help us tailor your community experience
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Playing Level */}
        <div className="bg-walnut border border-lv-border p-5 sm:p-6">
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-2">
            Playing Level
          </label>
          <select
            value={form.playing_level}
            onChange={handleSelect("playing_level")}
            className="lv-input"
            data-testid="preferences-level"
          >
            {LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Preferred Side */}
        <div className="bg-walnut border border-lv-border p-5 sm:p-6">
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-2">
            Preferred Side
          </label>
          <select
            value={form.preferred_side}
            onChange={handleSelect("preferred_side")}
            className="lv-input"
            data-testid="preferences-side"
          >
            {SIDE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Typical Playing Time */}
        <div className="bg-walnut border border-lv-border p-5 sm:p-6">
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-2">
            Typical Playing Time
          </label>
          <select
            value={form.typical_playing_time}
            onChange={handleSelect("typical_playing_time")}
            className="lv-input"
            data-testid="preferences-time"
          >
            {TIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Interests */}
        <div className="bg-walnut border border-lv-border p-5 sm:p-6">
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-3">
            Interests
          </label>
          <div className="flex flex-wrap gap-2" data-testid="preferences-interests">
            {INTEREST_OPTIONS.map((interest) => {
              const selected = form.interests.includes(interest);
              return (
                <button
                  key={interest}
                  type="button"
                  onClick={() => toggleInterest(interest)}
                  className={`px-3 py-1.5 text-xs tracking-[0.1em] uppercase transition-colors border ${
                    selected
                      ? "bg-bronze/20 border-bronze/40 text-cream"
                      : "bg-transparent border-lv-border text-lv-muted hover:border-bronze/30"
                  }`}
                  data-testid={`preferences-interest-${interest.toLowerCase()}`}
                >
                  {interest.replace("_", " ")}
                </button>
              );
            })}
          </div>
        </div>

        {/* Open to game invitations */}
        <div className="bg-walnut border border-lv-border p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-cream tracking-wide font-medium">
                Open to Game Invitations
              </p>
              <p className="text-xs text-sand/60 leading-relaxed mt-1">
                When enabled, you may appear in game suggestions to other members
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  open_to_game_invitations: !prev.open_to_game_invitations,
                }))
              }
              className={`w-11 h-6 rounded-full flex items-center px-0.5 shrink-0 transition-colors duration-200 ${
                form.open_to_game_invitations
                  ? "bg-lv-active/30 justify-end"
                  : "bg-lv-border justify-start"
              }`}
              data-testid="preferences-game-invitations-toggle"
            >
              <div
                className={`w-5 h-5 rounded-full transition-colors duration-200 ${
                  form.open_to_game_invitations ? "bg-lv-active" : "bg-lv-muted/50"
                }`}
              />
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={mutation.isPending}
          className="lv-btn-primary w-full min-h-[48px]"
          data-testid="preferences-save"
        >
          {mutation.isPending ? "Saving\u2026" : "Save Preferences"}
        </button>
      </form>
    </div>
  );
}
